import { useLayoutEffect, useRef, type FocusEvent } from "react";

/** Return a removed item's keyboard focus to its nearest surviving sibling. */
export function useCollectionFocusRecovery(
  keys: readonly string[],
  fallback: () => HTMLElement | null,
) {
  const ref = useRef<HTMLDivElement>(null);
  const previousKeys = useRef(keys);
  const focused = useRef<{
    key: string;
    index: number;
    element: HTMLElement;
  } | null>(null);
  useLayoutEffect(() => {
    const previous = focused.current;
    const index = previous ? previousKeys.current.indexOf(previous.key) : -1;
    if (previous && index >= 0) previous.index = index;
    previousKeys.current = keys;
    if (!previous || keys.includes(previous.key)) return;
    const frame = requestAnimationFrame(() => {
      focused.current = null;
      const active = document.activeElement;
      if (
        active !== document.body &&
        active !== previous.element &&
        active?.isConnected &&
        !active.closest('[role="dialog"][data-motion-state="closing"]')
      )
        return;
      const key = keys[Math.min(Math.max(0, previous.index), keys.length - 1)];
      const item =
        key === undefined
          ? null
          : Array.from(
              ref.current?.querySelectorAll<HTMLElement>("[data-focus-key]") ??
                [],
            ).find((node) => node.dataset.focusKey === key);
      const control =
        item?.querySelector<HTMLElement>(
          'button:not([disabled]), textarea:not([disabled]), [tabindex="0"]',
        ) ??
        item ??
        fallback();
      control?.focus();
    });
    return () => cancelAnimationFrame(frame);
  }, [keys, fallback]);
  return {
    ref,
    onFocusCapture: (event: FocusEvent<HTMLDivElement>) => {
      const element = event.target as HTMLElement;
      const key =
        element.closest<HTMLElement>("[data-focus-key]")?.dataset.focusKey;
      if (key !== undefined)
        focused.current = { key, index: keys.indexOf(key), element };
    },
  };
}
